import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getAllFaqItems,
  saveFaqDrafts,
} from '../services/landingFaqService';
import type { LandingFaqDraft } from '../types/landingFaq';
import { LANDING_FAQ_CATEGORIES } from '../types/landingFaq';
import AutoStatusToast from '../components/common/AutoStatusToast';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import type { AdminLandingTabActionsChange } from './adminLandingTabActions';

function newDraftId() {
  return `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function SortableFaqSection({
  draft,
  index,
  isEditing,
  onStartEdit,
  onStopEdit,
  onChange,
  onRemove,
}: {
  draft: LandingFaqDraft;
  index: number;
  isEditing: boolean;
  onStartEdit: (id: string) => void;
  onStopEdit: (id: string) => void;
  onChange: (id: string, patch: Partial<LandingFaqDraft>) => void;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: draft.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };

  const headId = `cp-admin-landing-faq-head-${draft.id}`;
  const label = draft.question.trim() || `질문 ${index + 1}`;
  const categoryLabel = draft.category.trim();

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`cp-dashboard-block cp-admin-landing-section-block${
        isDragging ? ' is-dragging' : ''
      }${draft.is_active ? '' : ' is-section-inactive'}`}
      aria-labelledby={headId}
    >
      <h3 className="cp-dashboard-block__head" id={headId}>
        <span className="cp-admin-landing-section-head-side cp-admin-landing-section-head-side--left">
          <button
            type="button"
            className={`cp-admin-landing-faq-check cp-admin-landing-section-active-check${
              draft.is_active ? ' is-done' : ''
            }`}
            title={draft.is_active ? '활성 (클릭하여 비활성)' : '비활성 (클릭하여 활성)'}
            aria-pressed={draft.is_active}
            aria-label={`${label} 활성 여부`}
            onClick={() => onChange(draft.id, { is_active: !draft.is_active })}
          >
            {draft.is_active ? (
              <svg
                className="cp-admin-landing-cta-check-mark"
                viewBox="0 0 20 20"
                width="13"
                height="13"
                aria-hidden
                focusable="false"
              >
                <path
                  d="M5.05 10.35 L9.25 14.65 L15.35 5.55"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.15"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
          </button>
          <button
            type="button"
            className="cp-admin-landing-faq-drag"
            title="드래그하여 순서 변경"
            aria-label={`${label} 순서 변경`}
            {...attributes}
            {...listeners}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
          </button>
        </span>
        <span className="cp-dashboard-block__head-title">
          Q&amp;A
          {categoryLabel ? (
            <span className="cp-admin-landing-section-order">{categoryLabel}</span>
          ) : null}
          <span className="cp-admin-landing-section-order">순서 {index + 1}</span>
        </span>
        <span className="cp-admin-landing-section-head-side cp-admin-landing-section-head-side--actions">
          {isEditing ? (
            <button
              type="button"
              className="cp-btn-secondary cp-admin-landing-edit-btn"
              onClick={() => onStopEdit(draft.id)}
            >
              편집 완료
            </button>
          ) : (
            <button
              type="button"
              className="cp-btn-secondary cp-admin-landing-edit-btn"
              onClick={() => onStartEdit(draft.id)}
            >
              편집
            </button>
          )}
          <button
            type="button"
            className="cp-admin-landing-faq-remove"
            onClick={() => onRemove(draft.id)}
            title="삭제"
            aria-label={`${label} 삭제`}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/Trash.svg`}
              alt=""
              width={14}
              height={14}
              className="cp-todo-note-trash-icon"
            />
          </button>
        </span>
      </h3>

      <div className="cp-dashboard-block__body cp-admin-landing-section-body">
        {isEditing ? (
          <div className="cp-admin-landing-faq-edit">
            <label className="cp-admin-landing-field">
              <span className="cp-admin-landing-field-label">범주</span>
              <select
                className="cp-admin-landing-edit-input"
                value={draft.category}
                onChange={(e) => onChange(draft.id, { category: e.target.value })}
              >
                <option value="">(선택 안 함)</option>
                {LANDING_FAQ_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
                {draft.category &&
                !(LANDING_FAQ_CATEGORIES as readonly string[]).includes(draft.category) ? (
                  <option value={draft.category}>{draft.category}</option>
                ) : null}
              </select>
            </label>
            <label className="cp-admin-landing-field">
              <span className="cp-admin-landing-field-label">질문</span>
              <textarea
                className="cp-admin-landing-edit-input cp-admin-landing-faq-question"
                value={draft.question}
                onChange={(e) => onChange(draft.id, { question: e.target.value })}
                rows={2}
                placeholder="질문"
              />
            </label>
            <label className="cp-admin-landing-field">
              <span className="cp-admin-landing-field-label">답변</span>
              <textarea
                className="cp-admin-landing-edit-input cp-admin-landing-faq-answer"
                value={draft.answer}
                onChange={(e) => onChange(draft.id, { answer: e.target.value })}
                rows={4}
                placeholder="답변"
              />
            </label>
          </div>
        ) : (
          <div className="cp-admin-landing-faq-preview">
            {categoryLabel ? (
              <p className="cp-admin-landing-faq-preview-cat">{categoryLabel}</p>
            ) : null}
            <p className="cp-admin-landing-faq-preview-q">{draft.question.trim() || '질문 없음'}</p>
            <p className="cp-admin-landing-faq-preview-a">
              {draft.answer.trim() || '답변 없음'}
            </p>
          </div>
        )}
      </div>
    </article>
  );
}

const AdminLandingFaqEditor: React.FC<{
  onActionsChange?: AdminLandingTabActionsChange;
}> = ({ onActionsChange }) => {
  const [drafts, setDrafts] = useState<LandingFaqDraft[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toastState: saveToastState, showSuccess: showSaveSuccess } = useAutoStatusToast(3000);

  const applyServer = useCallback((items: Awaited<ReturnType<typeof getAllFaqItems>>) => {
    setDrafts(
      items.map((it) => ({
        id: it.id,
        category: it.category ?? '',
        question: it.question,
        answer: it.answer,
        is_active: it.is_active,
      })),
    );
    setDeletedIds([]);
    setDirty(false);
    setEditingId(null);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllFaqItems();
      applyServer(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Q&A를 불러올 수 없습니다. 마이그레이션 적용 여부를 확인해 주세요.');
    } finally {
      setLoading(false);
    }
  }, [applyServer]);

  useEffect(() => {
    void load();
  }, [load]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const ids = useMemo(() => drafts.map((d) => d.id), [drafts]);

  const markDirty = () => setDirty(true);

  const patchDraft = (id: string, patch: Partial<LandingFaqDraft>) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    markDirty();
  };

  const removeDraft = (id: string) => {
    const target = drafts.find((d) => d.id === id);
    if (!target) return;
    if (!window.confirm('이 질문을 삭제할까요?')) return;
    setDrafts((prev) => prev.filter((d) => d.id !== id));
    if (!target.isNew) {
      setDeletedIds((prev) => [...prev, id]);
    }
    setEditingId((prev) => (prev === id ? null : prev));
    markDirty();
  };

  const addDraft = useCallback(() => {
    const id = newDraftId();
    setDrafts((prev) => [
      ...prev,
      {
        id,
        category: '서비스 안내',
        question: '',
        answer: '',
        is_active: true,
        isNew: true,
      },
    ]);
    setEditingId(id);
    markDirty();
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDrafts((prev) => {
      const oldIndex = prev.findIndex((d) => d.id === active.id);
      const newIndex = prev.findIndex((d) => d.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
    markDirty();
  };

  const handleDiscard = useCallback(() => {
    if (dirty && !window.confirm('저장하지 않은 변경을 모두 취소할까요?')) return;
    void load();
  }, [dirty, load]);

  const handleSave = useCallback(async () => {
    if (!dirty) return;
    try {
      setSaving(true);
      const fresh = await saveFaqDrafts(drafts, deletedIds);
      applyServer(fresh);
      showSaveSuccess('Q&A가 저장되었습니다.');
    } catch (err) {
      console.error(err);
      window.alert('저장에 실패했습니다. Q&A 마이그레이션 적용 여부를 확인해 주세요.');
    } finally {
      setSaving(false);
    }
  }, [applyServer, deletedIds, dirty, drafts, showSaveSuccess]);

  useEffect(() => {
    if (!onActionsChange) return;
    if (loading || error) {
      onActionsChange(null);
      return;
    }
    onActionsChange({
      dirty,
      saving,
      onDiscard: handleDiscard,
      onSave: () => {
        void handleSave();
      },
      onAdd: addDraft,
      addLabel: '질문 추가',
    });
    return () => onActionsChange(null);
  }, [addDraft, dirty, error, handleDiscard, handleSave, loading, onActionsChange, saving]);

  if (loading) {
    return <p className="cp-dashboard-placeholder" role="status">로딩 중…</p>;
  }

  if (error) {
    return (
      <div>
        <p className="cp-dashboard-placeholder" role="alert">
          {error}
        </p>
        <button type="button" className="cp-btn-primary" onClick={() => void load()}>
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="cp-admin-landing-faq">
      <AutoStatusToast state={saveToastState} />
      {dirty ? (
        <p className="cp-admin-landing-dirty-hint" role="status">
          저장되지 않은 변경이 있습니다. 「저장하기」를 눌러 반영하세요.
        </p>
      ) : null}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="cp-admin-landing-sections">
            {drafts.length === 0 ? (
              <p className="cp-dashboard-placeholder">질문이 없습니다. 「질문 추가」로 등록하세요.</p>
            ) : (
              drafts.map((draft, index) => (
                <SortableFaqSection
                  key={draft.id}
                  draft={draft}
                  index={index}
                  isEditing={editingId === draft.id}
                  onStartEdit={setEditingId}
                  onStopEdit={() => setEditingId(null)}
                  onChange={patchDraft}
                  onRemove={removeDraft}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default AdminLandingFaqEditor;
